import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock, Teleport as _Teleport } from "vue"

const _hoisted_1 = { class: "document-viewer" }
const _hoisted_2 = { class: "document-viewer__info" }
const _hoisted_3 = { class: "document-viewer__file-title" }
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = { class: "document-viewer__file-details" }
const _hoisted_6 = { class: "document-viewer__file-name" }
const _hoisted_7 = { class: "document-viewer__file-meta" }
const _hoisted_8 = {
  key: 0,
  class: "document-viewer__file-size"
}
const _hoisted_9 = { class: "document-viewer__file-type" }
const _hoisted_10 = { class: "document-viewer__info-grid" }
const _hoisted_11 = { class: "document-viewer__info-item" }
const _hoisted_12 = {
  key: 0,
  class: "document-viewer__info-item"
}
const _hoisted_13 = { class: "document-viewer__info-item" }
const _hoisted_14 = { class: "document-viewer__preview-actions" }
const _hoisted_15 = ["innerHTML"]
const _hoisted_16 = { class: "document-viewer__actions" }

import { ref, computed } from 'vue';
// @ts-ignore
import { onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/composables/useStore';
import { usePermissions } from '@/composables/usePermissions';
import { PermissionType } from '@/services/permission.service';
import AppModal from '../../base/Modal.vue';
import AppButton from '../../base/AppButton.vue';
import FilePreview from './FilePreview.vue';
import DocumentAnnotator from './DocumentAnnotator.vue';
import DocumentService from '@/services/documents.service';
import type { Document } from '@/types/services';

interface Props {
  projectId: string | number;
  document: Document;
}

interface Emits {
  (e: 'close'): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DocumentViewerModal',
  props: {
    projectId: {},
    document: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

/// <reference types="vue" />
// @ts-ignore
const { t } = useI18n();
const store = useStore();
const { hasPermissionForProject } = usePermissions();

const props = __props;

const emit = __emit;

const loading = ref<boolean>(true);
const error = ref<string | null>(null);
const documentUrl = ref<string | null>(null);
const showAnnotator = ref<boolean>(false);

// Check file type to determine if we should try loading a preview
const fileType = computed<string>(() => {
  const fileName = (props.document as any).fileName || props.document.originalName || props.document.name || '';
  return fileName.split('.').pop()?.toLowerCase() || '';
});

const isPreviewable = computed(() => {
  const previewableTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'pdf', 'txt', 'svg'];
  return previewableTypes.includes(fileType.value);
});

// Format date
const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString(store.getters.getCurrentLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format file size
const formatFileSize = (bytes: number | undefined): string => {
  if (!bytes || bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Get file icon based on extension
const getFileIcon = (filename: string | undefined): string => {
  if (!filename) return '';

  const extension = filename.split('.').pop()?.toLowerCase();

  const iconMap: Record<string, string> = {
    'pdf': '<i class="fas fa-file-pdf" style="color: #e74c3c; font-size: 3rem;"></i>',
    'doc': '<i class="fas fa-file-word" style="color: #2980b9; font-size: 3rem;"></i>',
    'docx': '<i class="fas fa-file-word" style="color: #2980b9; font-size: 3rem;"></i>',
    'xls': '<i class="fas fa-file-excel" style="color: #27ae60; font-size: 3rem;"></i>',
    'xlsx': '<i class="fas fa-file-excel" style="color: #27ae60; font-size: 3rem;"></i>',
    'ppt': '<i class="fas fa-file-powerpoint" style="color: #e67e22; font-size: 3rem;"></i>',
    'pptx': '<i class="fas fa-file-powerpoint" style="color: #e67e22; font-size: 3rem;"></i>',
    'jpg': '<i class="fas fa-file-image" style="color: #3498db; font-size: 3rem;"></i>',
    'jpeg': '<i class="fas fa-file-image" style="color: #3498db; font-size: 3rem;"></i>',
    'png': '<i class="fas fa-file-image" style="color: #3498db; font-size: 3rem;"></i>',
    'gif': '<i class="fas fa-file-image" style="color: #3498db; font-size: 3rem;"></i>',
    'zip': '<i class="fas fa-file-archive" style="color: #8e44ad; font-size: 3rem;"></i>',
    'rar': '<i class="fas fa-file-archive" style="color: #8e44ad; font-size: 3rem;"></i>',
    'txt': '<i class="fas fa-file-alt" style="color: #7f8c8d; font-size: 3rem;"></i>',
    'mp3': '<i class="fas fa-file-audio" style="color: #f39c12; font-size: 3rem;"></i>',
    'mp4': '<i class="fas fa-file-video" style="color: #d35400; font-size: 3rem;"></i>',
  };

  return extension ? (iconMap[extension] || '<i class="fas fa-file" style="color: #95a5a6; font-size: 3rem;"></i>') : '';
};

// Load document
const loadDocument = async () => {
  if (!props.projectId || !props.document?.id) {
    error.value = t('errors.invalidDocumentView');
    loading.value = false;
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    // Only try to load preview for files we can display in browser
    if (isPreviewable.value) {    // Get document data for viewing
      const result = await DocumentService.getDocumentForView(
        props.projectId, 
        props.document.id
      );
      
      // The data returned should be a Blob directly from our updated service
      if (result && result.data instanceof Blob) {
        documentUrl.value = URL.createObjectURL(result.data);
      } else {
        console.error('Invalid response format - expected a Blob object');
        error.value = t('errors.invalidResponseFormat');
      }
    }
    
    loading.value = false;
  } catch (err: any) {
    console.error('Error loading document:', err);
    error.value = err.message || t('errors.loadingDocumentError');
    loading.value = false;
  }
};

// Download the document
const downloadDocument = async (): Promise<void> => {
  try {
    await DocumentService.downloadDocument(
      props.projectId, 
      props.document.id, 
      (props.document as any).fileName || props.document.originalName || props.document.name
    );
  } catch (err: any) {
    console.error('Error downloading document:', err);
    store.dispatch('notifications/showError', t('errors.downloadingFileError'));
  }
};

// Check if user can annotate (requires UPLOAD_DOCUMENT permission)
const canAnnotate = computed(() => {
  const annotableTypes = ['jpg', 'jpeg', 'png', 'pdf', 'gif', 'webp', 'bmp'];
  return annotableTypes.includes(fileType.value) && 
         hasPermissionForProject(PermissionType.UPLOAD_DOCUMENT, props.projectId).value;
});

// Open annotator
const openAnnotator = () => {
  console.log('Opening annotator...', {
    documentUrl: documentUrl.value,
    projectId: props.projectId,
    documentId: props.document.id,
    fileType: fileType.value
  });
  
  if (!documentUrl.value) {
    console.error('Document URL is null - cannot open annotator');
    store.dispatch('notifications/showError', t('errors.loadingDocumentError'));
    return;
  }
  
  showAnnotator.value = true;
};

// Close annotator
const closeAnnotator = () => {
  console.log('Closing annotator...');
  showAnnotator.value = false;
};

// Handle annotation saved
const handleAnnotationSaved = () => {
  store.dispatch('notifications/showSuccess', t('annotation.savedSuccessfully'));
};

// Lifecycle hooks
onMounted(() => {
  loadDocument();
});

onUnmounted(() => {
  // Clean up blob URL if it exists
  if (documentUrl.value) {
    URL.revokeObjectURL(documentUrl.value);
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(AppModal, {
      show: true,
      onClose: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('close'))),
      title: (props.document as any).fileName || props.document.originalName || props.document.name,
      size: "large",
      "show-close": true,
      "no-padding": true
    }, {
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_16, [
          (canAnnotate.value)
            ? (_openBlock(), _createBlock(AppButton, {
                key: 0,
                type: "button",
                variant: "success",
                size: "medium",
                onClick: openAnnotator,
                disabled: loading.value
              }, {
                default: _withCtx(() => [
                  _cache[2] || (_cache[2] = _createElementVNode("i", { class: "fas fa-pencil-alt" }, null, -1)),
                  _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.annotate')), 1)
                ]),
                _: 1
              }, 8, ["disabled"]))
            : _createCommentVNode("", true),
          _createVNode(AppButton, {
            type: "button",
            variant: "outline",
            size: "medium",
            onClick: downloadDocument,
            disabled: loading.value
          }, {
            default: _withCtx(() => [
              _cache[3] || (_cache[3] = _createElementVNode("i", { class: "fas fa-download" }, null, -1)),
              _createTextVNode(" " + _toDisplayString(_ctx.$t('documents.download')), 1)
            ]),
            _: 1
          }, 8, ["disabled"]),
          _createVNode(AppButton, {
            type: "button",
            variant: "primary",
            size: "medium",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close')))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('common.close')), 1)
            ]),
            _: 1
          })
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("div", {
                innerHTML: getFileIcon((props.document as any).fileName || props.document.originalName || props.document.name),
                class: "document-viewer__file-icon"
              }, null, 8, _hoisted_4),
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("h4", _hoisted_6, _toDisplayString((props.document as any).fileName || props.document.originalName || props.document.name), 1),
                _createElementVNode("div", _hoisted_7, [
                  ((props.document as any).fileSize || props.document.size)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(formatFileSize((props.document as any).fileSize || props.document.size)), 1))
                    : _createCommentVNode("", true),
                  _createElementVNode("span", _hoisted_9, _toDisplayString(fileType.value.toUpperCase()), 1)
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("strong", null, _toDisplayString(_ctx.$t('documents.documentType')) + ":", 1),
                _createTextVNode(" " + _toDisplayString(_ctx.$t(`documents.types.${(props.document as any).docType || props.document.type}`) || (props.document as any).docType || props.document.type), 1)
              ]),
              ((props.document as any).description)
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                    _createElementVNode("strong", null, _toDisplayString(_ctx.$t('documents.description')) + ":", 1),
                    _createTextVNode(" " + _toDisplayString((props.document as any).description), 1)
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_13, [
                _createElementVNode("strong", null, _toDisplayString(_ctx.$t('documents.uploadDate')) + ":", 1),
                _createTextVNode(" " + _toDisplayString(formatDate((props.document as any).createdAt || props.document.uploadDate)), 1)
              ])
            ])
          ]),
          _createVNode(FilePreview, {
            fileName: (props.document as any).fileName || props.document.originalName || props.document.name,
            fileUrl: documentUrl.value || '',
            fileType: fileType.value,
            loading: loading.value,
            error: error.value || '',
            class: "document-viewer__preview"
          }, {
            "error-actions": _withCtx(() => [
              _createElementVNode("div", _hoisted_14, [
                _createVNode(AppButton, {
                  variant: "outline",
                  size: "small",
                  onClick: loadDocument
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('common.retry')), 1)
                  ]),
                  _: 1
                })
              ])
            ]),
            "file-icon": _withCtx(() => [
              _createElementVNode("div", {
                innerHTML: getFileIcon((props.document as any).fileName || props.document.originalName || props.document.name)
              }, null, 8, _hoisted_15)
            ]),
            _: 1
          }, 8, ["fileName", "fileUrl", "fileType", "loading", "error"])
        ])
      ]),
      _: 1
    }, 8, ["title"]),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (showAnnotator.value)
        ? (_openBlock(), _createBlock(DocumentAnnotator, {
            key: 0,
            projectId: props.projectId,
            documentId: props.document.id,
            documentUrl: documentUrl.value || '',
            fileName: (props.document as any).fileName || props.document.originalName || props.document.name,
            fileType: fileType.value,
            onClose: closeAnnotator,
            onSaved: handleAnnotationSaved
          }, null, 8, ["projectId", "documentId", "documentUrl", "fileName", "fileType"]))
        : _createCommentVNode("", true)
    ]))
  ]))
}
}

})